import { useNavigate, useParams } from 'react-router';
import { AppError } from '@st/contract';
import { readingMinutes } from '@st/markdown';
import { useContentArticle } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { Prose } from '../../components/Prose';
import { IconChevronLeft, IconDoc, IconLock } from '../../components/icons';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatDate } from '../../lib/format';
import { categoryLabelKey } from './Education';
import { Button, Card, SkeletonCard } from '../../ui';

/**
 * Članak.
 *
 * Zaključan članak vraća 402 — to NIJE greška ekrana nego stanje pristupa, pa
 * vodi na kupnju, ne na `ErrorState`. Isti dogovor vrijedi za zaključan trening
 * (`LockedWorkout`), da korisnik uvijek vidi isti izlaz iz istog zida.
 *
 * Tijelo je Markdown iz baze i prolazi kroz `Prose` (parser + naša tipografija),
 * nikad kroz `dangerouslySetInnerHTML` — vidi komentar u `Prose.tsx`.
 */
export function EducationArticle() {
  const { slug = '' } = useParams();
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const query = useContentArticle(slug);

  /*
    Povratak stoji i na vrhu i na dnu: na vrhu je izlaz prije čitanja, na dnu
    nakon njega. Oba su uokvirena — samostalna kontrola na praznoj plohi bez
    okvira ne čita se kao gumb nego kao tekst.
  */
  const backButton = (
    <Button
      variant="secondary"
      leading={<IconChevronLeft width={16} height={16} />}
      onClick={() => void navigate('/edukacija')}
    >
      {t('education.backToList')}
    </Button>
  );

  if (query.isError && query.error instanceof AppError && query.error.code === 'payment_required') {
    return (
      <>
        <PageHeader eyebrow={t('education.title')} title={t('education.lockedTitle')} />
        <Card data-testid="education-paywall">
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <span aria-hidden="true" className="text-accent">
              <IconLock width={40} height={40} />
            </span>
            <p className="max-w-prose text-[14.5px] leading-relaxed text-ink-muted">
              {t('education.lockedHelp')}
            </p>
            <div className="mt-1 flex flex-wrap justify-center gap-3">
              <Button onClick={() => void navigate('/pretplata')}>{t('education.unlock')}</Button>
              {backButton}
            </div>
          </div>
        </Card>
      </>
    );
  }

  return (
    <QueryBoundary query={query} skeleton={<SkeletonCard />}>
      {(article) => {
        const minutes = readingMinutes(article.body);
        const meta = [
          article.publishedAt ? formatDate(article.publishedAt, locale) : null,
          minutes > 0 ? t('education.readingTime', { n: minutes }) : null,
        ].filter((v): v is string => v !== null);

        return (
          <article>
            <div className="mb-5">{backButton}</div>

            <PageHeader
              eyebrow={t(categoryLabelKey(article.category))}
              title={article.title}
              {...(meta.length > 0 ? { subtitle: meta.join(' · ') } : {})}
            />

            {article.coverUrl && (
              <img
                src={article.coverUrl}
                alt=""
                className="mb-6 max-h-80 w-full rounded-xl object-cover"
              />
            )}

            <Card className="px-6 py-8 md:px-10 md:py-11">
              {article.body ? (
                <Prose body={article.body} />
              ) : (
                <p className="text-[14.5px] text-ink-muted">{t('education.noBody')}</p>
              )}

              {article.fileUrl && (
                <a
                  href={article.fileUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-9 inline-flex items-center gap-2 rounded-pill border border-line-strong px-6 py-3 text-[14.5px] text-ink transition-colors hover:border-accent-dark hover:bg-bg-alt hover:text-accent-dark"
                >
                  <IconDoc width={18} height={18} />
                  {t('education.download')}
                </a>
              )}
            </Card>

            <div className="mt-6">{backButton}</div>
          </article>
        );
      }}
    </QueryBoundary>
  );
}
